/**
 * SinglePlayerGameView — Modern visual game board for single-player.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSinglePlayerGame } from '../hooks/useSinglePlayerGame';
import { useSoundEffects } from '../hooks/useSoundEffects';
import { useGameStore } from '../stores';
import { Card, CardBack } from './Card';

interface Props {
  difficulty: 'easy' | 'medium' | 'hard';
  roomCode: string;
}

export function SinglePlayerGameView({ difficulty, roomCode }: Props) {
  const { startSinglePlayer, makeCall, playCard, resetGame } = useSinglePlayerGame();
  const { play: playSound } = useSoundEffects();
  const navigate = useNavigate();
  const started = useRef(false);
  const prevPhaseRef = useRef<string>('');

  const phase = useGameStore((s) => s.phase);
  const players = useGameStore((s) => s.players);
  const hand = useGameStore((s) => s.hand);
  const trickCards = useGameStore((s) => s.trickCards);
  const currentTurnIndex = useGameStore((s) => s.currentTurnIndex);
  const roundNumber = useGameStore((s) => s.roundNumber);
  const error = useGameStore((s) => s.error);
  const winnerId = useGameStore((s) => s.winnerId);
  const finalScores = useGameStore((s) => s.finalScores);
  const trickWinnerId = useGameStore((s) => s.trickWinnerId);

  const currentPlayer = players[currentTurnIndex];
  const isHumanTurn = currentPlayer?.id === 'human';
  const humanPlayer = players.find((p) => p.id === 'human');
  const opponents = players.filter((p) => p.id !== 'human');

  const seatPositions = ['top', 'left', 'right'] as const;
  const [collectDirection, setCollectDirection] = useState<
    'bottom' | (typeof seatPositions)[number] | null
  >(null);

  // When a trick is won, pause briefly to show the winning trick, then slide
  // the played cards toward the winner's seat before clearing the table.
  useEffect(() => {
    if (!trickWinnerId) {
      setCollectDirection(null);
      return;
    }

    const seat =
      trickWinnerId === 'human'
        ? 'bottom'
        : (seatPositions[opponents.findIndex((p) => p.id === trickWinnerId)] ?? 'bottom');

    const collectTimer = setTimeout(() => setCollectDirection(seat), 900);
    return () => clearTimeout(collectTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trickWinnerId]);

  useEffect(() => {
    if (!started.current) {
      started.current = true;
      startSinglePlayer(difficulty);
    }
  }, [difficulty, startSinglePlayer]);

  useEffect(() => {
    if (phase === prevPhaseRef.current) return;
    prevPhaseRef.current = phase;
    if (phase === 'playing' && isHumanTurn) playSound('yourTurn');
    if (phase === 'gameOver') playSound('gameOver');
  }, [phase, isHumanTurn, playSound]);

  const handleBack = () => {
    resetGame();
    navigate('/play/single');
  };

  if (phase === 'idle' || players.length === 0) {
    return (
      <div className="game-board game-board-loading">
        <div className="game-topbar">
          <button
            className="topbar-btn"
            onClick={handleBack}
            title="Back"
            aria-label="Back to menu"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        <div className="loading-indicator game-loading-indicator" role="status" aria-live="polite">
          Starting game...
        </div>
      </div>
    );
  }

  return (
    <div className="game-board">
      {/* Top Bar */}
      <div className="game-topbar">
        <button className="topbar-btn" onClick={handleBack} title="Back" aria-label="Back to menu">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="topbar-center">
          <span className="topbar-round">Round {roundNumber}</span>
          <span className="topbar-divider" />
          {roundNumber === 1 && phase === 'playing' ? (
            <span
              className="topbar-phase topbar-open-round"
              title="Round 1 has no calling phase — all tricks count"
            >
              Open
            </span>
          ) : (
            <span className="topbar-phase">
              {phase === 'playing' ? 'Playing' : phase === 'calling' ? 'Calling' : phase}
            </span>
          )}
          <span className="topbar-divider" />
          <span className="topbar-trump">♠ Trump</span>
        </div>
        <span className="topbar-room">{roomCode}</span>
      </div>

      {error && (
        <div className="game-toast game-toast-error" role="alert">
          {error}
        </div>
      )}

      {/* Felt Table */}
      <div className="felt-table">
        {/* Opponents */}
        {opponents.map((opp, idx) => {
          const seat = seatPositions[idx] ?? 'top';
          const isTurn = opp.id === currentPlayer?.id;
          return (
            <div
              key={opp.id}
              className={`opponent-seat opponent-${seat} ${isTurn ? 'is-turn' : ''}`}
            >
              <div className="opponent-cards">
                {Array.from({ length: opp.handSize ?? hand.length ?? 5 }).map((_, i) => (
                  <CardBack
                    key={i}
                    size="sm"
                    className="opponent-card-back"
                    style={
                      {
                        '--fan-index': i,
                        '--fan-total': opp.handSize ?? hand.length ?? 5,
                      } as React.CSSProperties
                    }
                  />
                ))}
              </div>
              <div className="opponent-info">
                <div className={`opponent-avatar ${isTurn ? 'avatar-glow' : ''}`}>
                  {opp.name.charAt(0)}
                </div>
                <span className="opponent-name">{opp.name}</span>
                <span className="opponent-score">
                  {opp.call ?? '-'}/{opp.tricksWon}
                </span>
              </div>
            </div>
          );
        })}

        {/* Center Trick Area */}
        <div className="trick-center">
          {trickCards.length === 0 && (phase === 'playing' || phase === 'calling') && (
            <span className="trick-empty-text">
              {isHumanTurn ? 'Your Turn' : `${currentPlayer?.name}'s Turn`}
            </span>
          )}
          <div
            className={`trick-played-cards ${collectDirection ? `trick-collecting trick-collect-${collectDirection}` : ''}`}
          >
            {trickCards.map((tc, idx) => (
              <div
                key={`${tc.card.suit}-${tc.card.rank}`}
                className="trick-played-card"
                style={
                  {
                    '--trick-index': idx,
                    '--trick-total': trickCards.length,
                  } as React.CSSProperties
                }
              >
                <Card suit={tc.card.suit} rank={tc.card.rank} size="lg" />
                <span className="trick-played-label">
                  {players.find((p) => p.id === tc.playerId)?.name}
                </span>
              </div>
            ))}
          </div>
          {trickWinnerId && (
            <div className="trick-winner-badge anim-fade-in">
              {trickWinnerId === 'human'
                ? 'You'
                : players.find((p) => p.id === trickWinnerId)?.name}{' '}
              win{trickWinnerId === 'human' ? '' : 's'}!
            </div>
          )}
        </div>
      </div>

      {/* Call Panel Overlay */}
      {phase === 'calling' && isHumanTurn && (
        <div
          className="call-overlay anim-slide-up"
          role="dialog"
          aria-modal="true"
          aria-labelledby="call-title"
        >
          <div className="call-glass">
            <h3 id="call-title" className="call-title">
              Make Your Call
            </h3>
            <div className="call-chips">
              {Array.from({ length: 12 }, (_, i) => i + 2).map((call) => (
                <button
                  key={call}
                  className="call-chip"
                  onClick={() => {
                    playSound('callMade');
                    makeCall(call);
                  }}
                >
                  {call}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Player Hand */}
      <div className="hand-dock">
        <div className="hand-player-info">
          <div className={`hand-avatar ${isHumanTurn ? 'avatar-glow' : ''}`}>You</div>
          <span className="hand-score">
            {humanPlayer?.call ?? '-'}/{humanPlayer?.tricksWon ?? 0} &middot;{' '}
            {humanPlayer?.totalScore ?? 0} pts
          </span>
        </div>
        <div className="hand-fan">
          {hand.map((card, idx) => (
            <Card
              key={`${card.suit}-${card.rank}`}
              suit={card.suit}
              rank={card.rank}
              size="lg"
              playable={isHumanTurn && phase === 'playing'}
              disabled={!isHumanTurn || phase !== 'playing'}
              onClick={() => {
                if (isHumanTurn && phase === 'playing') {
                  playSound('cardPlay');
                  playCard(card.suit, card.rank);
                }
              }}
              className={`hand-fan-card anim-deal anim-deal-${idx}`}
              style={
                {
                  '--fan-index': idx,
                  '--fan-total': hand.length,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      </div>

      {/* Waiting Indicator */}
      {(phase === 'calling' || phase === 'playing') && !isHumanTurn && (
        <div className="turn-waiting" role="status" aria-live="polite">
          <div className="waiting-dots">
            <span />
            <span />
            <span />
          </div>
          Waiting for {currentPlayer?.name}...
        </div>
      )}

      {/* Game Over */}
      {phase === 'gameOver' && winnerId && finalScores && (
        <div className="result-overlay">
          <div className="result-glass anim-celebrate">
            <h2 className="result-title">
              {winnerId === 'human'
                ? 'Victory!'
                : `${players.find((p) => p.id === winnerId)?.name} Wins`}
            </h2>
            <div className="result-trophy">{winnerId === 'human' ? '🏆' : '🎮'}</div>
            <div className="result-scores">
              {Object.entries(finalScores)
                .sort(([, a], [, b]) => b - a)
                .map(([pid, score], idx) => (
                  <div
                    key={pid}
                    className={`result-row ${pid === winnerId ? 'result-winner' : ''}`}
                  >
                    <span className="result-rank">#{idx + 1}</span>
                    <span className="result-name">
                      {players.find((p) => p.id === pid)?.name ?? pid}
                    </span>
                    <span className="result-pts">{score} pts</span>
                  </div>
                ))}
            </div>
            <div className="result-actions">
              <button
                className="btn-game btn-game-primary"
                onClick={() => {
                  resetGame();
                  startSinglePlayer(difficulty);
                  started.current = true;
                }}
              >
                Play Again
              </button>
              <button className="btn-game btn-game-outline" onClick={handleBack}>
                Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
